import {
    animate,
    state,
    style,
    transition,
    trigger,
} from "@angular/animations";
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    ViewChild,
    forwardRef,
} from "@angular/core";
import {
    ControlValueAccessor,
    FormControl,
    NG_VALUE_ACCESSOR,
} from "@angular/forms";
import { Store } from "@ngrx/store";
import { AutoUnsubscribe } from "ngx-auto-unsubscribe";
import {
    UploadFile,
    UploadInput,
    UploadOutput,
    UploaderOptions,
    humanizeBytes,
} from "ngx-uploader";
import { Subscription } from "rxjs";
import { environment } from "../../../src/environments/environment";
import { FormsEvents } from "../../enum/tracking/forms-events.enum";
import { AppState } from "../../interfaces/app-state.interface";
import { SettingsService } from "../../services/settings/settings.service";
import { TrackingActions } from "../../state-management/tracking/tracking.action";

/**
 * <file-upload [maxUploadFiles]='1'
 *              [componentId]=' 'upload-file__test_1' '
 *              [boxClass]=' 'single' '
 *              [btnText]=' 'frente' '
 *              [object]='>
 * </file-upload>
 *
 * Parametros da chamada do componenet -->
 *
 * maxUploadFiles - Número maximo de upload
 *
 * componentId - Id do componente, unica para cada upload
 *
 * boxClass: single | multiple - Tipo do upload
 *
 * btnText - Texto que aparecera no botão de upload
 *
 * object - objeto(model) que recebera o upload (EM DEV)
 */

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FileUploadComponent),
    multi: true,
};

@AutoUnsubscribe()
@Component({
    selector: "file-upload",
    templateUrl: "./file-upload.component.html",
    styleUrls: ["./file-upload.component.scss"],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
    animations: [
        trigger("visibilityChanged", [
            state("true", style({ height: "*", "padding-top": "4px" })),
            state("false", style({ height: "0px", "padding-top": "0px" })),
            transition("*=>*", animate("200ms")),
        ]),
    ],
})
export class FileUploadComponent
    implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy
{
    @Input() public control: FormControl = new FormControl();
    @Input() public minUploadFiles: number = 0;
    @Input() public maxUploadFiles: number;
    @Input() public componentId: string;
    @Input() public boxClass: string;
    @Input() public btnText?: string = "Adicione";
    @Input() public typeID?: number = 1;
    @Input() public object: string;
    @Input() public urlRoute: string;
    @Output() public initupload: EventEmitter<any> = new EventEmitter();
    @Output() public endupload: EventEmitter<any> = new EventEmitter();
    @ViewChild("input", { static: true }) public inputRef: ElementRef;

    public percentUpload: number = 0;
    public flag = true;
    public files: UploadFile[];
    public uploadInput: EventEmitter<UploadInput>;
    public uploadOptions: UploaderOptions;
    public fileReady: EventEmitter<boolean>;
    public fileReadySubscription: Subscription;
    public authenticationSubscription: Subscription;
    public controlSusbcription: Subscription;
    public humanizeBytes: any;
    public errors: any[] = ["Envie o(s) arquivo(s)"];
    public fileDone: boolean = false;
    private innerValue: any = "";
    private token: string;
    private fileBase64: string;

    constructor(
        public elementRef: ElementRef,
        private settingsService: SettingsService,
        private store: Store<AppState>
    ) {
        this.files = []; // local uploading files array
        this.uploadInput = new EventEmitter<UploadInput>(); // input events, we use this to emit data to ngx-uploader

        this.uploadOptions = {
            concurrency: 0,
        };

        this.fileReady = new EventEmitter<boolean>();

        this.fileReadySubscription = this.fileReady.subscribe((data) => {
            this.fileDone = data;
            if (data) {
                this.control.setValue(this.innerValue);
            }
        });

        this.humanizeBytes = humanizeBytes;
        this.authenticationSubscription = this.store
            .select((s) => s.authentication)
            .subscribe((authentication) => {
                if (!authentication.currentUser) return;
                this.token = authentication.currentUser.token;
            });
    }

    public ngOnInit() {
        this.inputRef.nativeElement.multiple =
            this.boxClass === "multiple" ? true : false;
    }

    /* UPLOAD LIFECICLY */
    public onUploadOutput(output: UploadOutput): void {
        if (output.type === "allAddedToQueue") {
            // when all files added in queue
            // uncomment this if you want to auto upload files when added
        } else if (
            output.type === "addedToQueue" &&
            typeof output.file !== "undefined"
        ) {
            // add file to array when added
            this.flag = true;
            this.previewImagem(output.file.nativeFile).then(
                (responseBase64) => {
                    const file = output.file as any;

                    file.preview = (file.type as string).includes("application")
                        ? `url('assets/images/pdf-thumb.png')`
                        : `url(${responseBase64})`;
                    this.files.push(file);
                    let documentTypeGuid = `${this.urlRoute.split("/")[4]}`;

                    //Necessário para testar em hml. A lista de document types da api de hml só contempla esse guid em hml
                    if (!environment.production) {
                        documentTypeGuid =
                            "870C005A-C174-4353-851E-9627D1197B90";
                    }
                    const url = this.urlRoute
                        .replace(this.urlRoute.split("/")[4], "")
                        .trim();
                    const event: UploadInput = {
                        type: "uploadAll",
                        url: this.settingsService.baseUrl(url),
                        method: "POST",
                        data: {
                            fileNameWithExtension: `${file.nativeFile.name}`,
                            fileBase64: `${responseBase64}`,
                            documentTypeGuid: `${documentTypeGuid}`,
                        },
                        headers: {
                            Authorization: "Bearer " + this.token,
                            "Content-Type": "application/json",
                        },
                    };
                    //console.log(event);
                    this.uploadInput.emit(event);
                    this.fileReady.emit(false);
                    this.initupload.emit(true);
                    this.control.setValue(null);
                    this.errors = [];
                }
            );
            this.fileReady.emit(false);
            this.errors = [];
        } else if (
            output.type === "uploading" &&
            typeof output.file !== "undefined"
        ) {
            this.percentUpload = output.file.progress.data.percentage;
            const index = this.files.findIndex(
                (file) =>
                    typeof output.file !== "undefined" &&
                    file.id === output.file.id
            );

            this.onChange(null, output.file);
            if (this.flag && index === -1) {
                this.flag = false;
            }
            this.files[index] = output.file;
            this.fileReady.emit(false);
            this.errors = [];
        } else if (output.type === "removed") {
            this.files = this.files.filter(
                (file: UploadFile) => file !== output.file
            );
            this.errors = [];
        } else if (output.type === "done") {
            if (
                output.file.responseStatus !== 200 ||
                output.file.response.hasError
            ) {
                this.control.setErrors({ uploadFail: true });
                this.control.markAsTouched();
                this.errors = this.formatUploadError(output.file.response);
                this.fileReady.emit(false);
                this.files.splice(output.file.fileIndex, 1);
                this.inputRef.nativeElement.value = null;
                this.endupload.emit({});

                // Track erro
                const controls = this.control.parent.controls;
                const controlName =
                    Object.keys(controls).find(
                        (name) => this.control === controls[name]
                    ) || null;

                this.store.dispatch({
                    type: TrackingActions.trackFormEvent,
                    payload: {
                        type: "Erro",
                        event:
                            FormsEvents[controlName] ||
                            `erro_upload_${controlName}`,
                    },
                });

                return;
            } else if (output.file && output.file.responseStatus === 200) {
                this.setErrors({ uploadFile: null, required: null });
                this.control.setValue(true);
                this.control.updateValueAndValidity();
                this.errors = [];
                this.endupload.emit({
                    file: output.file.response.value,
                    typeID: this.typeID,
                }); // Emit fileDTO
                this.fileReady.emit(true);
            }
        }
    }

    public formatUploadError(error: any = { messages: [] }) {
        const errorMessages = {
            "StartIndex cannot be less than zero.":
                "Arquivo inválido, tente outro arquivo",
        };

        return error.messages.map(({ message }) => {
            const errorMessage = errorMessages[message];

            if (errorMessage) return errorMessage;

            return message;
        });
    }
    /* UPLOAD LIFECICLY */

    public setErrors(obj) {
        const errors = { ...{}, ...this.control.errors, ...obj };
        this.control.setErrors(errors);
    }

    /* ACCESS VALUE */
    // tslint:disable-next-line:no-empty
    public ngOnChanges() {}

    // event fired when input value is changed . later propagated up to the form control using the custom value accessor interface
    public onChange(e: Event, value: any) {
        // set changed value
        this.innerValue = value;
        // propagate value into form control using control value accessor interface
        this.propagateChange(this.innerValue);
        // this.c.setValue(this.innerValue);
        // reset errors
        this.errors = [];
        // setting, resetting error messages into an array (to loop) and adding the validation messages to show below the field area
        for (const key in this.control.errors) {
            if (this.control.errors.hasOwnProperty(key)) {
                if (key === "required") {
                    this.errors.push("Por favor, envie a imagem");
                } else {
                    this.errors.push(this.control.errors[key]);
                }
            }
        }
    }

    // get accessor
    public get value(): any {
        return this.innerValue;
    }

    // set accessor including call the onchange callback
    public set value(v: any) {
        if (v !== this.innerValue) {
            this.innerValue = v;
        }
    }

    // propagate changes into the custom form control
    // tslint:disable-next-line:no-empty
    public propagateChange = (_: any) => {};

    // From ControlValueAccessor interface
    public writeValue(value: any) {
        this.innerValue = value;
    }

    // From ControlValueAccessor interface
    public registerOnChange(fn: any) {
        this.propagateChange = fn;
    }

    // From ControlValueAccessor interface
    // tslint:disable-next-line:no-empty
    public registerOnTouched(fn: any) {}
    /* ACCESS VALUE */

    public ngAfterViewInit() {
        // RESET the custom input form control UI when the form control is RESET
        this.controlSusbcription = this.control.valueChanges.subscribe(() => {
            if (
                this.control.value === "" ||
                this.control.value == null ||
                this.control.value === undefined
            ) {
                this.innerValue = "";
                this.inputRef.nativeElement.value = "";
            }
        });
    }

    /* EMITTERS */
    public cancelUpload(id: string): void {
        this.uploadInput.emit({ type: "cancel", id });
    }

    public removeFile(id: string): void {
        this.uploadInput.emit({ type: "remove", id });
    }

    public removeAllFiles(): void {
        this.uploadInput.emit({ type: "removeAll" });
    }
    /* EMITTERS */

    public previewImagem(file: any) {
        const fileReader = new FileReader();
        return new Promise((resolve) => {
            fileReader.readAsDataURL(file);
            fileReader.onload = (e: any) => {
                resolve(e.target.result);
            };
        });
    }

    public getFormattedFileName(nome: string) {
        return nome.length > 18
            ? nome.substring(0, 10) +
                  "..." +
                  nome.substring(nome.length - 15 + 10)
            : nome;
    }

    public removePreview($event, i) {
        $event.preventDefault();
        $($event.target).parent().parent().addClass("fadeOut");

        setTimeout(() => {
            this.files.splice(i, 1);
            this.inputRef.nativeElement.value = null;
            this.onChange(null, null);
        }, 500);

        this.fileReady.emit(false);
        this.control.setValue(null);
    }

    // não remover, necessário para o funcionamento correto do @AutoUnsubscribe
    // tslint:disable-next-line:no-empty
    public ngOnDestroy() {}

    // tslint:disable-next-line:no-empty
    public handlePreviewData(event) {}
}
